import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { isoDurationSchema } from "../isoDuration";
import { paginatedSchema } from "../pagination";

import { organisationSchema } from "./organisations";

export const rasterSourceListItemSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  url: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  organisation: organisationSchema,
  access_modifier: z.string(),
  supplier: z.string().nullable(),
  supplier_code: z.string().nullable(),
  temporal: z.boolean(),
  interval: isoDurationSchema.nullable(),
  first_value_timestamp: lizardDateSchema.nullable(),
  last_value_timestamp: lizardDateSchema.nullable(),
  spatial_bounds: z
    .object({
      west: z.number(),
      east: z.number(),
      north: z.number(),
      south: z.number(),
    })
    .nullable(),
  projection: z.string().nullable(),
  origin_x: z.number().nullable(),
  origin_y: z.number().nullable(),
  pixelsize_x: z.number().nullable(),
  pixelsize_y: z.number().nullable(),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
  writable: z.boolean(),
  layers: z.array(z.string()),
  labeltypes: z.array(z.string()),
  size: z.number().nullable(),
});

export type RasterSourceListItem = z.infer<typeof rasterSourceListItemSchema>;

export const rasterSourceListSchema = paginatedSchema(rasterSourceListItemSchema);
export type RasterSourceList = z.infer<typeof rasterSourceListSchema>;

export const rasterSourceSchema = rasterSourceListItemSchema;
export type RasterSource = RasterSourceListItem;

export function useRasterSources(params = {}) {
  const url = combineUrlAndParams("/api/v4/rastersources/", params);
  return useParsedQuery<RasterSourceList>(
    ["lizard-api-v4", "rastersources", url],
    () => fetchWithError(url),
    (json: unknown) => rasterSourceListSchema.parse(json)
  );
}

export function useRasterSource(uuid: string) {
  return useParsedQuery<RasterSource>(
    ["lizard-api-v4", "rastersources", uuid],
    () => fetchWithError(`/api/v4/rastersources/${uuid}/`),
    (json: unknown) => rasterSourceSchema.parse(json)
  );
}

export function useMultipleRasterSources(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "rastersources", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/rastersources/${uuid}/`),
        parseFunction: (json: unknown) => rasterSourceSchema.parse(json),
      };
    })
  );
}
